import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Camera, X, Loader2, Eye, AlertTriangle, ShieldAlert, Plus, ImageOff } from "lucide-react";
import { api, API } from "@/lib/api";

/**
 * CLEANBAZ Before/After Photo Documentation
 *
 * Real working upload UI wired to the existing endpoints:
 *   1) POST /upload (multipart) -> { path, size }
 *   2) POST /jobs/:id/photos { phase, url, category, notes?, is_existing_damage? }
 *   3) GET  /jobs/:id/photos  { before_photos[], after_photos[], documentation_disclaimer }
 *
 * mode="capture"  -> cleaner-only capture UI with 6 category slots + upload/remove.
 * mode="view"     -> read-only thumbnails + notes + timestamps for customer/admin/history.
 * phase="before"|"after"
 */

// Category slots. Same list is rendered for both Before and After so the
// cleaner is encouraged to shoot from similar angles.
const BEFORE_CATS = [
  { id: "front",           label: "Front / Main Area" },
  { id: "left",            label: "Left Side / Area" },
  { id: "right",           label: "Right Side / Area" },
  { id: "interior",        label: "Interior / Main Area" },
  { id: "additional",      label: "Additional Photo", extra: true },
  { id: "existing_damage", label: "Existing Damage", damage: true },
];
const AFTER_CATS = [
  { id: "front",      label: "Front / Main Area" },
  { id: "left",       label: "Left Side / Area" },
  { id: "right",      label: "Right Side / Area" },
  { id: "interior",   label: "Interior / Main Area" },
  { id: "additional", label: "Additional Photo", extra: true },
];

function fileUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  const tok = localStorage.getItem("token") || "";
  return `${API}/files/${path}?auth=${encodeURIComponent(tok)}`;
}

async function uploadFile(file) {
  const form = new FormData();
  form.append("file", file);
  const tok = localStorage.getItem("token") || "";
  const res = await fetch(`${API}/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${tok}` },
    body: form,
  });
  if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
  return res.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// One photo slot: click to shoot / upload. When populated -> thumbnail + remove.
// ─────────────────────────────────────────────────────────────────────────────
function PhotoSlot({ slot, phase, jobId, existing, onChange, disabled, notesValue, onNotesChange }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const filled = !!existing;
  const isDamage = slot.damage === true;

  const pick = () => fileInputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-picked after remove
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB per photo"); return; }
    setBusy(true);
    try {
      const up = await uploadFile(file);
      const { data } = await api.post(`/jobs/${jobId}/photos`, {
        phase,
        url: up.path,
        category: slot.id,
        is_existing_damage: isDamage,
        notes: isDamage ? (notesValue || "") : "",
      });
      onChange(slot.id, data.entry);
      toast.success(`${slot.label} — uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!existing?.id) return;
    setBusy(true);
    try {
      await api.delete(`/jobs/${jobId}/photos/${existing.id}`);
      onChange(slot.id, null);
      toast.success("Photo removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`photo-slot-${phase}-${slot.id}`}
      className={`rounded-2xl border-2 p-3 flex flex-col transition-all ${
        filled
          ? isDamage
            ? "border-red-300 bg-red-50/60"
            : "border-teal-300 bg-teal-50/60"
          : "border-dashed border-slate-300 hover:border-teal-400 bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        {isDamage ? (
          <AlertTriangle className="w-4 h-4 text-red-600" />
        ) : (
          <Camera className="w-4 h-4 text-teal-600" />
        )}
        <div className="text-xs font-mono uppercase tracking-widest text-slate-600 flex-1 truncate">
          {slot.label}
        </div>
        {filled && (
          <span className="text-[9px] font-mono uppercase tracking-widest text-teal-600">✓ Uploaded</span>
        )}
      </div>

      {!filled ? (
        <button
          type="button"
          onClick={pick}
          disabled={disabled || busy}
          data-testid={`photo-add-${phase}-${slot.id}`}
          className={`aspect-video rounded-xl border border-slate-200 bg-white flex flex-col items-center justify-center gap-1.5 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
            isDamage ? "hover:border-red-300 hover:bg-red-50/40" : "hover:border-teal-400"
          }`}
        >
          {busy ? (
            <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
          ) : (
            <>
              <Camera className={`w-6 h-6 ${isDamage ? "text-red-500" : "text-teal-500"}`} />
              <span className="text-xs font-semibold text-slate-700">Add Photo</span>
              <span className="text-[10px] text-slate-400">JPG · PNG · HEIC · up to 8 MB</span>
            </>
          )}
        </button>
      ) : (
        <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-900 group">
          <img
            src={fileUrl(existing.url)}
            alt={slot.label}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent flex items-center justify-between text-[10px] text-white font-mono">
            <span>{new Date(existing.uploaded_at).toLocaleTimeString()}</span>
            <span className="uppercase">{isDamage ? "Damage" : phase}</span>
          </div>
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPreviewOpen(true)}
              data-testid={`photo-preview-${phase}-${slot.id}`}
              className="bg-white/90 text-slate-900 h-8"
            >
              <Eye className="w-3.5 h-3.5 mr-1" />Preview
            </Button>
            {!disabled && (
              <Button
                size="sm"
                variant="destructive"
                onClick={remove}
                disabled={busy}
                data-testid={`photo-remove-${phase}-${slot.id}`}
                className="h-8"
              >
                <X className="w-3.5 h-3.5 mr-1" />Remove
              </Button>
            )}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
        capture="environment"
        onChange={onFile}
        className="hidden"
        data-testid={`photo-file-${phase}-${slot.id}`}
      />

      {isDamage && (
        <Textarea
          value={notesValue || ""}
          onChange={(e) => onNotesChange(e.target.value)}
          data-testid={`photo-damage-notes-${phase}`}
          placeholder="Short description of the pre-existing damage (scratch on door, stain on rug, etc.)"
          className="mt-2 text-xs min-h-[64px]"
        />
      )}

      {previewOpen && filled && (
        <div
          role="dialog"
          className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewOpen(false)}
          data-testid={`photo-preview-modal-${phase}-${slot.id}`}
        >
          <img src={fileUrl(existing.url)} className="max-w-full max-h-full rounded-lg" alt={slot.label} />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The full documentation panel used inside Start/Complete dialogs AND for view.
// ─────────────────────────────────────────────────────────────────────────────
export default function JobPhotoDocumentation({ jobId, phase, mode = "capture", onReady }) {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [damageNotes, setDamageNotes] = useState("");
  const [extraSlots, setExtraSlots] = useState([]); // dynamically added additional photos

  const cats = phase === "before" ? BEFORE_CATS : AFTER_CATS;

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/jobs/${jobId}/photos`);
      setRecord(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load photos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId, phase]);

  // Index existing photos by category so slots know what's already there.
  const byCategory = useMemo(() => {
    const list = record ? (phase === "before" ? record.before_photos : record.after_photos) : [];
    const map = {};
    for (const p of list) {
      const key = p.category || (p.is_existing_damage ? "existing_damage" : "additional");
      if (!map[key]) map[key] = p;   // first photo per slot
      else if (!map[`__extra_${key}_${p.id}`]) map[`__extra_${key}_${p.id}`] = p;
    }
    return map;
  }, [record, phase]);

  useEffect(() => {
    if (!record) return;
    const required = phase === "before"
      ? cats.filter((c) => !c.extra && !c.damage).every((c) => byCategory[c.id])
      : cats.filter((c) => !c.extra).every((c) => byCategory[c.id]);
    onReady && onReady(required, record);
  }, [record, byCategory, phase, cats, onReady]);

  const onSlotChange = (catId, entry) => {
    // Re-fetch canonical list so timestamps stay authoritative.
    load();
    if (catId !== "existing_damage") return;
  };

  if (loading) return (
    <div className="flex items-center justify-center p-10 text-slate-500 text-sm">
      <Loader2 className="w-5 h-5 mr-2 animate-spin" />Loading photo record…
    </div>
  );

  // View mode: pure thumbnails + notes for customer/admin/history.
  if (mode === "view") {
    const list = phase === "before" ? record.before_photos : record.after_photos;
    if (!list?.length) {
      return (
        <div className="text-center py-8 text-slate-500">
          <ImageOff className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          <div className="text-sm">No {phase} photos on record.</div>
        </div>
      );
    }
    return (
      <div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {list.map((p) => (
            <div key={p.id || p.url} data-testid={`view-photo-${phase}-${p.id || 'x'}`} className={`rounded-xl overflow-hidden border ${p.is_existing_damage ? "border-red-300" : "border-slate-200"} bg-white`}>
              <div className="relative aspect-video bg-slate-100">
                <img src={fileUrl(p.url)} alt={p.category || phase} className="w-full h-full object-cover" />
                {p.is_existing_damage && (
                  <Badge className="absolute top-2 left-2 bg-red-600 text-white border-0 text-[10px] uppercase font-mono">
                    <AlertTriangle className="w-3 h-3 mr-1" />Existing Damage
                  </Badge>
                )}
              </div>
              <div className="p-2 text-[11px] text-slate-600">
                <div className="font-semibold text-slate-800 uppercase tracking-widest text-[9px]">
                  {p.category?.replace(/_/g, " ") || phase}
                </div>
                {p.notes && <div className="mt-0.5 italic">{p.notes}</div>}
                <div className="mt-0.5 font-mono text-[10px] text-slate-400">
                  {p.uploaded_at ? new Date(p.uploaded_at).toLocaleString() : "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
        {record.documentation_disclaimer && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900 flex items-start gap-2" data-testid="photo-disclaimer">
            <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{record.documentation_disclaimer}</span>
          </div>
        )}
      </div>
    );
  }

  // Capture mode (cleaner) — 6 slots + Add Another Photo.
  return (
    <div>
      <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-3 mb-4 text-xs text-teal-900 flex items-start gap-2">
        <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Privacy reminder:</strong> Photograph only the work area. Do not photograph people, personal documents,
          payment cards, screens, or unrelated private items. For homes, keep shots to areas required to document the cleaning.
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {cats.map((c) => (
          <PhotoSlot
            key={c.id}
            slot={c}
            phase={phase}
            jobId={jobId}
            existing={byCategory[c.id]}
            onChange={onSlotChange}
            notesValue={damageNotes}
            onNotesChange={setDamageNotes}
          />
        ))}

        {extraSlots.map((eid) => (
          <PhotoSlot
            key={eid}
            slot={{ id: `additional_${eid}`, label: "Extra Photo", extra: true }}
            phase={phase}
            jobId={jobId}
            existing={byCategory[`additional_${eid}`]}
            onChange={onSlotChange}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => setExtraSlots((s) => [...s, Date.now()])}
        data-testid={`photo-add-more-${phase}`}
        className="mt-4 w-full py-3 rounded-xl border-2 border-dashed border-slate-300 hover:border-teal-400 text-sm font-semibold text-slate-600 hover:text-teal-700 flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" />Add another photo
      </button>
    </div>
  );
}
